import { useCallback, useEffect, useRef, useState } from "react";
import { useWebPubSub } from "~/features/projectTop/webPubSubContext";
import type { ReverseEngineeringPhaseStatus } from "~/interface/reverse";
import {
  generateReverseEngineeringStatusGroupName,
  joinGroup,
  leaveGroup,
} from "~/utils/webPubSubUtils";

interface UseReverseEngineeringStatusOptions {
  timeoutMs?: number;
  onTimeout?: () => void;
  onError?: (error: Error) => void;
}

interface UseReverseEngineeringStatusResult {
  isConnected: boolean;
  error: Error | null;
  lastStatus: ReverseEngineeringPhaseStatus | null;
  retry: () => void;
}

export const useReverseEngineeringStatus = (
  instanceId: string | null,
  onStatusChange: (status: ReverseEngineeringPhaseStatus) => void,
  options: UseReverseEngineeringStatusOptions = {},
): UseReverseEngineeringStatusResult => {
  const { timeoutMs, onTimeout, onError } = options;
  const { client } = useWebPubSub();

  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastStatus, setLastStatus] = useState<ReverseEngineeringPhaseStatus | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastMessageTimeRef = useRef<number>(Date.now());
  const onStatusChangeRef = useRef(onStatusChange);
  const onTimeoutRef = useRef(onTimeout);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    lastMessageTimeRef.current = Date.now();
    if (timeoutMs && timeoutMs > 0) {
      timeoutRef.current = setTimeout(() => {
        const timeoutError = new Error(
          `WebPubSub timeout: No message received in ${timeoutMs / 60000} minutes`,
        );
        setError(timeoutError);
        onTimeoutRef.current?.();
        onErrorRef.current?.(timeoutError);
      }, timeoutMs);
    }
  }, [timeoutMs]);

  const connect = useCallback(async () => {
    if (!instanceId || !client) {
      return;
    }

    try {
      setError(null);
      const groupName = generateReverseEngineeringStatusGroupName(instanceId);
      await joinGroup(client, groupName);
      setIsConnected(true);
      resetTimeout();
    } catch (err) {
      const connectionError =
        err instanceof Error ? err : new Error("Failed to connect to WebPubSub");
      setError(connectionError);
      setIsConnected(false);
      onErrorRef.current?.(connectionError);
    }
  }, [instanceId, client, resetTimeout]);

  const retry = useCallback(() => {
    setError(null);
    connect();
  }, [connect]);

  useEffect(() => {
    if (!instanceId || !client) {
      return;
    }

    const groupName = generateReverseEngineeringStatusGroupName(instanceId);

    const handleMessage = (e: any) => {
      if (e.group && e.group !== groupName) {
        return;
      }

      try {
        const message = JSON.parse(e.message.data) as ReverseEngineeringPhaseStatus;
        setLastStatus(message);
        resetTimeout();
        onStatusChangeRef.current(message);
      } catch (parseError) {
        console.error("Failed to parse WebPubSub message:", parseError);
      }
    };

    (client as any).on("group-message", handleMessage);
    connect();

    return () => {
      (client as any).off("group-message", handleMessage);
      leaveGroup(client, groupName);
      setIsConnected(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [instanceId, client, connect, resetTimeout]);

  return {
    isConnected,
    error,
    lastStatus,
    retry,
  };
};
